import { Editor, Transforms } from 'slate';
import { ReactEditor, useSlate } from 'slate-react';
import { RTEUtils } from './RTEUtils';

/**
 * Custom hook `useRTE` for managing the rich text editor (RTE) functionality in Slate.
 *
 * Provides utility functions for:
 * - Focusing the editor (`focus`), with optional delay.
 * - Inserting text at the current selection or at the start if no selection exists (`addText`).
 * - Resetting the editor to its empty state (`resetEditor`), with optional focus after reset.
 *
 * Returns:
 * - `addText`: Function to add text to the editor.
 * - `resetEditor`: Function to reset the editor's content to its initial state.
 * - `focus`: Function to focus the editor, optionally after a delay.
 */
export const useRTE = () => {
  const editor = useSlate();

  const focus = (focusDelay?: number) => {
    if (typeof focusDelay !== 'undefined')
      setTimeout(() => {
        ReactEditor.focus(editor as ReactEditor);
      }, focusDelay);
  };

  const addText = (text: string) => {
    if (text) {
      const { selection: newSelection } = editor;

      // if the editor is not focused or there's no active selection
      if (!newSelection) {
        // set the selection to the start of the document
        Transforms.select(editor, {
          anchor: { path: [0, 0], offset: 0 },
          focus: { path: [0, 0], offset: 0 },
        });
      }

      Editor.insertText(editor, text);
    }
  };

  const resetEditor = ({ focusDelay }: { focusDelay?: number } = {}) => {
    Transforms.delete(editor, {
      at: {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      },
    });
    Transforms.removeNodes(editor, {
      at: [0],
    });
    Transforms.insertNodes(editor, RTEUtils.emptyEditorState);

    focus(focusDelay);
  };

  return { addText, resetEditor, focus };
};
